// What a check-in's greetingResult (from /checkin/create) says about the family being told.
// Shared by the home Hello! tile and the Greetings page so both read the result the same way.
export interface CheckInGreetingResult {
    success?: boolean;
    greetingSent?: boolean;
    reason?: string;
    recipientCount?: number;
    // Whether the resident has a live Daily Greetings subscription. null/undefined means the
    // server could not tell (lookup failed, or the facility has card payments off).
    entitled?: boolean | null;
}

// The part of a /checkin/create or /checkin/update response the outcome is read from.
export interface CheckInResponse {
    status?: string;
    greetingResult?: CheckInGreetingResult;
}

export type CheckInOutcome =
    // The greeting went out to at least one contact.
    | 'sent'
    // Greetings is on but no active contact is set to receive it.
    | 'no_recipients'
    // Greetings is on without a subscription, or was never turned on and there is no subscription.
    | 'not_signed_up'
    // A live subscription whose Greetings switch is off (e.g. a cancellation waiting for period end).
    | 'switched_off'
    // Greetings is off and the server could not say why: the check-in simply succeeded.
    | 'not_enabled'
    // Every contact opted out or has a number that cannot get texts: retrying would not help.
    | 'undeliverable'
    // The check-in worked but the greeting did not go out (a send failure, or any reason not listed here).
    | 'send_failed'
    // A card for today already existed (another tab or device checked in first): nothing was sent.
    | 'already_checked_in'
    // The existing card was moved into checked_in through /checkin/update, which never sends a greeting.
    | 'moved_without_greeting'
    // /checkin/create answered without a greetingResult: the check-in worked, nothing is known about the greeting.
    | 'unknown';

export interface CheckInOutcomeResult {
    outcome: CheckInOutcome;
    // Only meaningful for 'sent'.
    recipientCount?: number;
}

// `route` is the call that produced the response: only /checkin/create sends a greeting
// (s2l checkinCreate -> /checkin/create); /checkin/update moves an existing card and never does.
export const getCheckInOutcome = (route: 'create' | 'update', response?: CheckInResponse | null): CheckInOutcomeResult => {
    if (route === 'update') return { outcome: 'moved_without_greeting' };

    const result = response?.greetingResult;
    if (!result) return { outcome: 'unknown' };
    if (result.reason === 'already_checked_in') return { outcome: 'already_checked_in' };
    if (result.greetingSent === true) {
        return {
            outcome: 'sent',
            recipientCount: typeof result.recipientCount === 'number' ? result.recipientCount : undefined,
        };
    }
    if (result.reason === 'not_entitled') return { outcome: 'not_signed_up' };
    if (result.reason === 'greeting_not_enabled') {
        if (result.entitled === false) return { outcome: 'not_signed_up' };
        if (result.entitled === true) return { outcome: 'switched_off' };
        return { outcome: 'not_enabled' };
    }
    if (result.reason === 'no_recipients') return { outcome: 'no_recipients' };
    if (result.reason === 'no_deliverable_recipients') return { outcome: 'undeliverable' };
    return { outcome: 'send_failed' };
};
